import { useEffect, useState } from 'react';
import { TrendingUp, Sparkles, AlertTriangle, CheckCircle2, Activity, Download } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, formatPercent, pad2 } from '@/lib/utils';
import type { LiveDataEntity, LiveReviewEntity, PlanningEntity } from '@shared/types';

export default function AnalyticsPage() {
  const [liveData, setLiveData] = useState<LiveDataEntity[]>([]);
  const [reviews, setReviews] = useState<LiveReviewEntity[]>([]);
  const [plannings, setPlannings] = useState<PlanningEntity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getLiveData(), api.getLiveReviews(), api.getPlannings()])
      .then(([ld, rv, pl]) => {
        setLiveData(ld);
        setReviews(rv);
        setPlannings(pl);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-foreground font-space uppercase tracking-widest text-sm py-32 text-center">LOADING<span className="text-primary">_</span></div>;
  }

  // 健康度评分
  const avgScore = reviews.length > 0 ? reviews.reduce((s, r) => s + r.score, 0) / reviews.length : 0;
  const healthScore = Math.round(avgScore);

  // 多维度
  const dimensions = reviews.length > 0 ? [
    { key: '流量', value: Math.round(reviews.reduce((s, r) => s + r.trafficScore, 0) / reviews.length) },
    { key: '转化', value: Math.round(reviews.reduce((s, r) => s + r.conversionScore, 0) / reviews.length) },
    { key: '互动', value: Math.round(reviews.reduce((s, r) => s + r.engagementScore, 0) / reviews.length) },
    { key: '内容', value: Math.round(reviews.reduce((s, r) => s + r.diagnosis.scores.content, 0) / reviews.length) },
    { key: '效率', value: Math.round(reviews.reduce((s, r) => s + r.diagnosis.scores.efficiency, 0) / reviews.length) }
  ] : [];

  // 趋势
  const dates = Array.from(new Set(liveData.map((l) => l.date))).sort();
  const gmvByDate = dates.map((d) => liveData.filter((l) => l.date === d).reduce((s, l) => s + l.totalGmv, 0));

  const trendOption = {
    backgroundColor: 'transparent',
    grid: { top: 30, right: 24, bottom: 30, left: 80 },
    tooltip: { trigger: 'axis', backgroundColor: '#000', borderColor: '#444', textStyle: { color: '#fff' } },
    xAxis: { type: 'category', data: dates.map((d) => dayjs(d).format('MM/DD')), axisLine: { lineStyle: { color: '#444' } }, axisLabel: { color: '#aaa' } },
    yAxis: { type: 'value', axisLine: { lineStyle: { color: '#444' } }, splitLine: { lineStyle: { color: '#222' } }, axisLabel: { color: '#aaa', formatter: (v: number) => formatCurrency(v) } },
    series: [{ type: 'bar', data: gmvByDate, itemStyle: { color: '#dafa1d' } }]
  };

  const ringOption = {
    backgroundColor: 'transparent',
    series: [{
      type: 'gauge',
      startAngle: 90,
      endAngle: -270,
      radius: '90%',
      progress: { show: true, width: 18, itemStyle: { color: '#dafa1d' } },
      axisLine: { lineStyle: { width: 18, color: [[1, '#222']] } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      pointer: { show: false },
      detail: { fontSize: 48, color: '#dafa1d', fontFamily: 'Space Grotesk', fontWeight: 700, formatter: '{value}' },
      data: [{ value: healthScore }]
    }]
  };

  const aiInsights = [
    {
      type: 'success',
      title: '整体运营健康度良好',
      content: `综合评分 ${healthScore}/100,处于 ${healthScore >= 80 ? '优秀' : healthScore >= 60 ? '良好' : '需改进'} 区间。继续保持现有节奏。`
    },
    {
      type: 'tip',
      title: '建议加强自然流量运营',
      content: '数据显示付费流量占比偏高,通过短视频预热+私域社群联动,有望将自然流量占比提升 5-10 个百分点。'
    },
    {
      type: 'warning',
      title: '关注晚间档流量波动',
      content: '近期 21:00 后观看人数下滑明显,建议优化该时段选品结构,引入秒杀/福袋留住观众。'
    },
    {
      type: 'info',
      title: '本周复盘已完成 ' + reviews.length + ' 场',
      content: '完成率 100%,AI 已为每场生成洞察报告,可在「数据复盘」中查看。'
    }
  ];

  const handleExport = () => {
    const csv = [
      ['日期', '场次', '主播', 'GMV', '观看', '转化率', '互动率'].join(','),
      ...liveData.map((l) => [
        l.date,
        l.title,
        l.hostName,
        l.totalGmv,
        l.viewCount,
        l.viewToConversionRate.toFixed(2),
        l.viewToInteractionRate.toFixed(2)
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `直播数据报表_${dayjs().format('YYYYMMDD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <header className="border-b-2 border-border pb-8 mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-space mb-2">
            ANALYTICS / {dayjs().format('YYYY.MM.DD')}
          </div>
          <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.85] tracking-tighter uppercase font-space">
            运营<br />分析<span className="text-primary">.</span>
          </h1>
        </div>
        <button
          onClick={handleExport}
          className="border-2 border-border px-4 py-3 font-bold uppercase tracking-widest text-sm hover:bg-primary hover:text-black hover:border-primary flex items-center gap-2"
        >
          <Download className="w-4 h-4" /> 导出报表
        </button>
      </header>

      {/* 健康度 */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-border mb-12">
        <div className="p-8 md:p-12 border-b-2 lg:border-b-0 lg:border-r-2 border-border">
          <div className="flex items-center gap-3 mb-6">
            <Activity className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-bold font-space uppercase">运营健康度</h2>
          </div>
          <ReactECharts option={ringOption} style={{ height: 300 }} />
          <div className="text-center text-xs text-muted-foreground uppercase tracking-widest">
            基于 {reviews.length} 场复盘综合评分
          </div>
        </div>
        <div className="p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-bold font-space uppercase">多维度评分</h2>
          </div>
          <div className="space-y-4">
            {dimensions.map((d) => (
              <div key={d.key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold uppercase tracking-widest">{d.key}</span>
                  <span className="font-space font-bold tabular-nums">{pad2(d.value)}<span className="text-xs text-muted-foreground">/100</span></span>
                </div>
                <div className="h-3 border-2 border-border">
                  <div
                    className={`h-full ${d.value >= 80 ? 'bg-primary' : d.value >= 60 ? 'bg-warning' : 'bg-destructive'}`}
                    style={{ width: `${d.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 长期趋势 */}
      <section className="border-2 border-border p-8 md:p-12 mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-4 h-4 bg-primary" />
          <h2 className="text-2xl font-bold font-space uppercase">GMV 长期趋势</h2>
        </div>
        <ReactECharts option={trendOption} style={{ height: 320 }} />
      </section>

      {/* 数据概览 */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-border mb-12">
        {[
          { label: '直播场次', value: liveData.length, unit: '场' },
          { label: '复盘记录', value: reviews.length, unit: '条' },
          { label: '策划活动', value: plannings.length, unit: '个' },
          { label: '总 GMV', value: formatCurrency(liveData.reduce((s, l) => s + l.totalGmv, 0)), unit: '' }
        ].map((s, i) => (
          <div key={i} className="border-r-2 last:border-r-0 border-border p-6 hover:bg-primary hover:text-black transition-colors duration-200">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{s.label}</div>
            <div className="text-3xl md:text-4xl font-bold font-space tabular-nums tracking-tighter">{s.value}</div>
            {s.unit && <div className="text-xs text-muted-foreground mt-1">{s.unit}</div>}
          </div>
        ))}
      </section>

      {/* AI 诊断 */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="text-2xl md:text-3xl font-bold font-space uppercase">AI 诊断建议</h2>
        </div>
        <div className="space-y-0 border-2 border-border">
          {aiInsights.map((insight, i) => (
            <div
              key={i}
              className={`p-6 md:p-8 border-b-2 last:border-b-0 border-border ${
                insight.type === 'success' ? 'border-l-4 border-l-primary' :
                insight.type === 'warning' ? 'border-l-4 border-l-destructive' :
                insight.type === 'tip' ? 'border-l-4 border-l-info' :
                'border-l-4 border-l-muted-foreground'
              } hover:bg-accent transition-colors duration-200`}
            >
              <div className="flex items-start gap-4">
                <span className="font-space font-bold text-2xl tabular-nums opacity-50">{pad2(i + 1)}</span>
                <div>
                  <div className="font-bold uppercase tracking-tight text-lg mb-2">{insight.title}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{insight.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
